import styles from "./address-modal.module.css";
import { Modal } from "BEDROCK/shared-components/modal";
import { CTAButton } from "./cta-button";
import { ToolboxComponentInput } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-input";
import { useState, useEffect } from "react";
import { useAddressStore, ShippingAddress } from "entity-store/address-store/use-address-store";
import { v4 as uuid } from "uuid";
import { ActivityIndicator } from "./activity-indicator";

interface AddressModalProps {
  isShowing: boolean;
  onConfirm: (address: ShippingAddress) => void;
  onDismiss: () => void;
  currentAddress?: ShippingAddress | null;
}

const validateUKPostcode = (postcode: string): boolean => {
  const ukPostcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
  return ukPostcodeRegex.test(postcode.trim());
};

export const AddressModal = ({
  isShowing,
  onConfirm,
  onDismiss,
  currentAddress,
}: AddressModalProps) => {
  const { defaultAddress, saveAddress, isLoadingDefault } = useAddressStore();
  const [useSaved, setUseSaved] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Load current or default address when modal opens
  useEffect(() => {
    if (isShowing) {
      if (currentAddress && defaultAddress && currentAddress.id === defaultAddress.id) {
        // Current address IS the saved default - show in display mode
        setUseSaved(true);
      } else if (currentAddress) {
        // There's a current address but it's NOT the saved one - show in edit mode
        setLine1(currentAddress.line1);
        setLine2(currentAddress.line2 || "");
        setCity(currentAddress.city);
        setPostcode(currentAddress.postcode);
        setUseSaved(false);
      } else if (defaultAddress) {
        // No current address, but there's a default - show it in display mode
        setUseSaved(true);
      } else {
        // No address at all - show empty form
        resetForm();
        setUseSaved(false);
      }
    }
  }, [isShowing, currentAddress, defaultAddress]);

  const resetForm = () => {
    setLine1("");
    setLine2("");
    setCity("");
    setPostcode("");
    setError(null);
  };

  const handleToggleMode = () => {
    if (useSaved && defaultAddress) {
      // Switching to new/different address - clear form
      resetForm();
      setUseSaved(false);
    } else if (!useSaved && defaultAddress) {
      // Switching back to saved default address - don't populate form fields
      // since we'll show it in display mode, just reset the form state
      resetForm();
      setUseSaved(true);
    }
  };

  const handleConfirm = async () => {
    // If using saved address, just confirm it directly
    if (useSaved) {
      const addressToConfirm = currentAddress || defaultAddress;
      if (addressToConfirm) {
        onConfirm(addressToConfirm);
        return;
      }
    }

    // Otherwise validate the form fields
    if (!line1.trim()) {
      setError("Address line 1 is required");
      return;
    }
    if (!city.trim()) {
      setError("City is required");
      return;
    }
    if (!postcode.trim()) {
      setError("Postcode is required");
      return;
    }

    // Validate UK postcode
    if (!validateUKPostcode(postcode)) {
      setError("Please enter a valid UK postcode");
      return;
    }

    setError(null);

    // Always save the address
    const addressData: ShippingAddress = {
      id: defaultAddress?.id || uuid(),
      user_id: "", // Will be set by backend
      line1: line1.trim(),
      line2: line2.trim() || undefined,
      city: city.trim(),
      postcode: postcode.trim().toUpperCase(),
      country: "GB",
      is_default: true,
      created_at: defaultAddress?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    try {
      setIsSaving(true);
      const saved = await saveAddress(addressData);
      onConfirm(saved);
    } catch (err) {
      console.error("Failed to save address:", err);
      setError("Failed to save address. Please try again.");
      setIsSaving(false);
      return;
    } finally {
      setIsSaving(false);
    }
  };

  // When using saved address, validation is based on whether we have a default address
  // When editing, validation is based on form fields
  const isValid = useSaved
    ? !!(currentAddress || defaultAddress)
    : line1.trim() &&
      city.trim() &&
      postcode.trim() &&
      validateUKPostcode(postcode);

  if (isLoadingDefault) {
    return (
      <Modal isOpen={isShowing} showCloseButton={false} onClose={onDismiss}>
        <div className={styles.container}>
          <ActivityIndicator />
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isShowing} showCloseButton={true} onClose={onDismiss}>
      <div className={styles.container}>
        <h1>Delivery Address</h1>
        <p className={styles.subtitle}>
          We currently only ship to UK addresses
        </p>

        {defaultAddress && (
          <div className={styles.toggleContainer}>
            <button
              className={styles.toggleButton}
              onClick={handleToggleMode}
              type="button"
            >
              {useSaved ? "Use a different address" : "Use saved address"}
            </button>
          </div>
        )}

        {useSaved && (currentAddress || defaultAddress) ? (
          <div className={styles.savedAddressDisplay}>
            <p className={styles.savedAddressLabel}>Delivering to:</p>
            <div className={styles.savedAddress}>
              <p>{(currentAddress || defaultAddress)!.line1}</p>
              {(currentAddress || defaultAddress)!.line2 && <p>{(currentAddress || defaultAddress)!.line2}</p>}
              <p>{(currentAddress || defaultAddress)!.city}</p>
              <p>{(currentAddress || defaultAddress)!.postcode}</p>
            </div>
          </div>
        ) : (
          <div className={styles.formContainer}>
            <ToolboxComponentInput
              text={line1}
              placeholder="Address Line 1"
              onChangeText={setLine1}
              variant="outlined"
            />
            <ToolboxComponentInput
              text={line2}
              placeholder="Address Line 2 (optional)"
              onChangeText={setLine2}
              variant="outlined"
            />
            <ToolboxComponentInput
              text={city}
              placeholder="City"
              onChangeText={setCity}
              variant="outlined"
            />
            <ToolboxComponentInput
              text={postcode}
              placeholder="Postcode"
              onChangeText={(value) => setPostcode(value.toUpperCase())}
              variant="outlined"
            />
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.buttonContainer}>
          <CTAButton
            variant="PRIMARY"
            onClick={handleConfirm}
            disabled={!isValid || isSaving}
          >
            {isSaving ? "Saving..." : "Confirm address"}
          </CTAButton>
        </div>
      </div>
    </Modal>
  );
};
